/**
 * useHealthData — owns the health-stats query, dedupe/search/group memos, and the
 * derived chart/summary aggregations. Extracted verbatim from HealthContainerModule.
 *
 * Query wiring: staleTime + refetchInterval are throttled to 30s (matching the 30s
 * cadence in useActivities.ts — staleTime + refetchInterval) so the Health module
 * doesn't hammer getObjectsByType('stat') ~20x/min while mounted (Refs #5255).
 *
 * Freshness after an agent write or a user action is event-driven via
 * `queryClient.invalidateQueries` against the STABLE `HEALTH_STATS_QUERY_KEY`
 * (#5578) — never a locally-scoped counter baked into the key. The Health module
 * fully unmounts on module-pane switch-away (no keep-alive), so a `useState` tick
 * counter resets to its initial value on every remount; embedding that counter in
 * the query key used to make each `refetch()` spin up a brand-new, separately
 * cached query entry, so a remount within the 30s staleTime window could
 * resubscribe to a STALE entry that predated the latest write and show missing
 * data for up to 30s. A single stable key has exactly one cache entry to keep
 * fresh, no matter how many times the module mounts/unmounts.
 */
import { useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query/keys';
import { api } from '@/lib/api';
import { buildChartData, buildSummaryCards } from '../utils/aggregate';

interface UseHealthDataArgs {
  token: string | null | undefined;
  workspaceId: string | undefined;
  searchQuery: string;
  /** active wearable provider — used for legacy-source synthesis in chartData */
  providerHint?: string;
}

/** Stable cache key for the health-stats list query (#5578) — MUST stay free of
 *  any locally-scoped counter/tick. Every "force a fresh read" caller (the
 *  agent-log listener below, the exposed `refetch()`, and HealthContainerModule's
 *  own direct `invalidateQueries` calls after create/update/delete) targets this
 *  SAME key so they all converge on one cache entry instead of fragmenting into
 *  several separately-staled ones. */
export const HEALTH_STATS_QUERY_KEY = [...queryKeys.objects.byType('stat'), 'health'];

export function useHealthData({ token, workspaceId, searchQuery, providerHint }: UseHealthDataArgs) {
  const queryClient = useQueryClient();

  // Force a fresh read when the agent logs health data (dispatched from ChatBox
  // on tool completion) or navigates to a specific date — invalidates the STABLE
  // key rather than bumping a key-embedded counter (#5578).
  useEffect(() => {
    const handler = () => {
      // Small delay to let the API commit the write
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: HEALTH_STATS_QUERY_KEY });
      }, 500);
    };
    window.addEventListener('health-navigate-date', handler);
    return () => window.removeEventListener('health-navigate-date', handler);
  }, [queryClient]);

  const { data: healthStats = [] } = useQuery({
    queryKey: HEALTH_STATS_QUERY_KEY,
    queryFn: async () => {
      const result = await api.objects.getObjectsByType(token ?? null, 'stat', { limit: 1000 });
      const stats = result?.items || [];
      return stats.filter((s: any) => {
        const attrs = s.attributes || {};
        const source = attrs.source || s.source || '';
        const category = attrs.category || s.category || '';
        if (source === 'health:profile') return false;
        // Include: wearable sources, health category, manual entries, and any integration-synced stats
        const HEALTH_CATEGORIES = new Set(['health', 'sleep', 'readiness', 'activity', 'heart', 'recovery', 'strain', 'workout', 'body', 'stress']);
        const WEARABLE_PROVIDERS = ['whoop', 'oura', 'fitbit', 'strava'];
        return WEARABLE_PROVIDERS.some(p => source.startsWith(p + ':')) || source.startsWith('health:') || HEALTH_CATEGORIES.has(category) || source === 'manual';
      });
    },
    enabled: Boolean(token && workspaceId),
    // 30s throttle (mirrors the 30s cadence in features/activities/useActivities.ts:
    // useActivitiesTab staleTime :111 + useActivitiesBadge refetchInterval :141).
    // Freshness after writes is event-driven via invalidateQueries (see above), so a
    // tight time-poll here is redundant — it only added ~20 background req/min (Refs #5255).
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

  // Deduplicate, sort, and filter by search query
  const allItems = useMemo(() => {
    const seen = new Set<string>();
    const deduped = healthStats
      .filter((item: any) => { if (seen.has(item.id)) return false; seen.add(item.id); return true; })
      .sort((a: any, b: any) => {
        const dateA = a.date || a.attributes?.date || a.createdAt || '';
        const dateB = b.date || b.attributes?.date || b.createdAt || '';
        return dateB.localeCompare(dateA);
      });
    if (!searchQuery.trim()) return deduped;
    const q = searchQuery.toLowerCase();
    return deduped.filter((item: any) => {
      const label = (item.title || item.label || '').toLowerCase();
      const source = (item.source || item.attributes?.source || '').toLowerCase();
      const meal = (item.meal || item.attributes?.meal || '').toLowerCase();
      const itemNames = (item.items || []).map((i: any) => (i.name || '').toLowerCase()).join(' ');
      return label.includes(q) || source.includes(q) || meal.includes(q) || itemNames.includes(q);
    });
  }, [healthStats, searchQuery]);

  // Group by date — show wearable data (Oura, Whoop, Fitbit, Strava), skip food/exercise "Untitled" items
  const grouped = useMemo(() => {
    const WEARABLE_PROVIDERS = ['whoop', 'oura', 'fitbit', 'strava'];
    const WEARABLE_CATEGORIES = new Set(['sleep', 'readiness', 'activity', 'heart', 'recovery', 'strain', 'workout', 'body', 'stress']);
    const wearableItems = allItems.filter((item: any) => {
      const source = item.source || item.attributes?.source || '';
      const category = item.category || item.attributes?.category || '';
      return WEARABLE_PROVIDERS.some(p => source.startsWith(p + ':')) || WEARABLE_CATEGORIES.has(category);
    });
    return wearableItems.reduce((acc: Record<string, any[]>, item: any) => {
      const date = (item.date || item.attributes?.date || item.createdAt || '').slice(0, 10);
      if (!acc[date]) acc[date] = [];
      acc[date].push(item);
      return acc;
    }, {});
  }, [allItems]);

  // Sparkline data — group by source for chart.
  // NOTE: dep array is [allItems] (NOT providerHint) to match the original component's
  // memo EXACTLY — the original read activeWearable?.provider with a [allItems]-only dep,
  // so legacy-source synthesis did not re-run on a bare wearable switch. Preserving that
  // verbatim keeps this decomposition behavior-preserving; fixing the latent staleness is
  // a separate follow-up, out of scope here.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const chartData = useMemo(() => buildChartData(allItems, providerHint), [allItems]);

  // Summary stats — 30-day averages, primary scores first
  const summaryCards = useMemo(() => buildSummaryCards(chartData), [chartData]);

  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: HEALTH_STATS_QUERY_KEY });
  };

  return { allItems, grouped, chartData, summaryCards, refetch };
}
